import React, { useState } from 'react';
import { Row, Col, Alert, List, ListInlineItem } from 'reactstrap';
import HTMLBlock from './HTMLBlock';
import TableBlock from './TableBlock';
import CallToAction from '../CallToAction/CallToAction';
import SurveyHost from '../../containers/Survey/SurveyHost';
import CampaignFlow from '../UI/CampaignFlow/CampaignFlow';
import RotationRenderer from './RotationRenderer';
import ResponsiveImageSet from '../UI/ResponsiveImageSet/ResponsiveImageSet';
import ImageCard from '../ImageCard/ImageCard';
import SocialShareBar from '../UI/SocialShareBar/SocialShareBar';
import MemoryGameContainer from '../../containers/MemoryGame/MemoryGameContainer';
import { renderMarkdownToReact } from '../UI/MarkdownText/MarkdownText';

const DismissibleAlert = ({ variant, title, text, dismissible, style }) => {
    const [visible, setVisible] = useState(true);
    if (!visible) return null;
    return (
        <Alert
            color={variant || 'info'}
            isOpen={visible}
            toggle={dismissible ? () => setVisible(false) : undefined}
            className="w-100"
            style={style}
        >
            {title && <h4 className="alert-heading">{title}</h4>}
            {text && <p className="mb-0">{renderMarkdownToReact(text)}</p>}
        </Alert>
    );
};

const isRotationAloneOnTopLevel = (blocks) => {
    if (!Array.isArray(blocks) || blocks.length !== 1) return false;
    const cols = blocks[0]?.columns || blocks[0]?.cols || [];
    if (cols.length !== 1) return false;
    const comps = cols[0]?.components || cols[0]?.comps || [];
    if (comps.length !== 1) return false;
    const comp = comps[0];
    const type = comp?.type || comp?.kind;
    return type === 'rotation';
};

const renderLeafComponent = (comp, index, totalInCol, isTopLevelAlone) => {
    if (!comp) return null;
    const type = comp.type || comp.kind;
    const props = comp.props || {};
    const key = comp.id || comp.key || index;
    const bottomFix = index + 1 === totalInCol ? { marginBottom: 0 } : {};

    switch (type) {
        case 'html':
            return <HTMLBlock key={key} html={props.html || comp.html || ''} />;
        case 'heading': {
            const HeadingTag = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(props.level) ? props.level : 'h2';
            return (
                <React.Fragment key={key}>
                    <HeadingTag>{props.text || ''}</HeadingTag>
                    {props.subline && <p className="lead text-muted">{renderMarkdownToReact(props.subline)}</p>}
                </React.Fragment>
            );
        }
        case 'paragraph': {
            const classes = props.isLead ? 'lead' : '';
            return (
                <p key={key} className={classes || undefined} style={bottomFix}>
                    {renderMarkdownToReact(props.text || '')}
                </p>
            );
        }
        case 'list': {
            const listTag = props.listType === 'ordered' ? 'ol' : 'ul';
            const rawItems = Array.isArray(props.items)
                ? props.items
                : typeof props.items === 'string'
                  ? props.items.split('\n')
                  : [];
            const filteredItems = rawItems.filter((item) =>
                typeof item === 'string' ? item.trim() !== '' : Boolean(item)
            );
            const listType = props.inline ? 'inline' : props.unstyled ? 'unstyled' : undefined;
            const ItemTag = props.inline ? ListInlineItem : 'li';

            return (
                <List key={key} tag={listTag} type={listType} style={bottomFix}>
                    {filteredItems.map((item, itemIdx) => (
                        <ItemTag key={itemIdx}>
                            {renderMarkdownToReact(
                                typeof item === 'object' && item !== null ? item.text || item.label : String(item)
                            )}
                        </ItemTag>
                    ))}
                </List>
            );
        }
        case 'quote': {
            return (
                <figure key={key} style={bottomFix}>
                    <blockquote className="blockquote">
                        <p>{renderMarkdownToReact(props.text || '')}</p>
                    </blockquote>
                    {props.author && <figcaption className="blockquote-footer">{props.author}</figcaption>}
                </figure>
            );
        }
        case 'alert': {
            return (
                <DismissibleAlert
                    key={key}
                    variant={props.variant}
                    title={props.title}
                    text={props.text}
                    dismissible={props.dismissible}
                    style={bottomFix}
                />
            );
        }
        case 'socialShare':
            return <SocialShareBar key={key} {...props} style={bottomFix} />;
        case 'cta':
            return <CallToAction key={key} {...props} style={bottomFix} />;
        case 'imageCard':
            return <ImageCard key={key} {...props} style={bottomFix} />;
        case 'responsiveImage':
            return (
                <ResponsiveImageSet
                    key={key}
                    breakpoints={props.breakpoints || []}
                    altText={props.altText !== undefined ? props.altText : ''}
                    style={bottomFix}
                    {...props}
                />
            );
        case 'survey':
            return <SurveyHost key={key} {...props} style={bottomFix} />;
        case 'memoryGame':
            return <MemoryGameContainer key={key} style={bottomFix} />;
        case 'campaignFlow':
            return <CampaignFlow key={key} {...props} style={bottomFix} />;
        case 'rotation':
            return <RotationRenderer key={key} node={comp} isTopLevelAlone={isTopLevelAlone} />;
        case 'table':
            return <TableBlock key={key} {...props} style={bottomFix} />;
        default:
            return null;
    }
};

const getColWidths = (col = {}) => ({
    xs: col.xs || 12,
    sm: col.sm,
    md: col.md,
    lg: col.lg,
});

const getOrderClasses = (col) => {
    if (!col) return '';
    const order = typeof col.order === 'object' && col.order !== null ? col.order.xs : (col.orderXs ?? col.order);

    if (order !== undefined && order !== null && order !== 'none') {
        const orderStr = String(order);
        const xsClass = orderStr.includes('order-') ? orderStr : `order-${orderStr}`;
        return `${xsClass} order-md-0`;
    }

    return '';
};

const PageBlockRenderer = ({ blocks, nodes }) => {
    const rowNodes = blocks || nodes;

    if (!Array.isArray(rowNodes) || rowNodes.length === 0) {
        return null;
    }

    const isAlone = isRotationAloneOnTopLevel(rowNodes);

    return (
        <div
            className="d-flex flex-column w-100"
            style={{ rowGap: '1rem' }} // Ensures gap spacing across row elements
        >
            {rowNodes.map((row, rowIndex) => {
                const rowKey = row.id || row.key || rowIndex;
                const columns = row.columns || row.cols || row.children || [];

                const rowClassName = [row.className, row.class, 'row-gap-3'].filter(Boolean).join(' ');

                return (
                    <Row key={rowKey} className={rowClassName} style={{ rowGap: '1rem' }}>
                        {columns.map((col, colIndex) => {
                            const colKey = col.id || col.key || colIndex;
                            const components = col.components || col.comps || col.children || col.items || [];
                            const { xs, sm, md, lg } = getColWidths(col);
                            const orderClass = getOrderClasses(col);

                            const defaultColClasses = ['d-flex', 'align-items-start', 'flex-column'];
                            const colClassName = [...defaultColClasses, orderClass, col.className, col.class]
                                .filter(Boolean)
                                .join(' ');

                            return (
                                <Col key={colKey} xs={xs} sm={sm} md={md} lg={lg} className={colClassName || undefined}>
                                    {components.map((comp, compIndex) =>
                                        renderLeafComponent(comp, compIndex, components.length, isAlone)
                                    )}
                                </Col>
                            );
                        })}
                    </Row>
                );
            })}
        </div>
    );
};

export default PageBlockRenderer;
